import React, { FC } from "react";
import { Stack, SxProps, Typography } from "@mui/material";
import { Link } from "gatsby-theme-material-ui";

import { MenuItemSize, MenuLink } from "@src/types";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors";
import ServiceIcon from "@src/components/ServiceIcon";
import {
  accentBackgrounds,
  accentColors,
  filterFooterNavItems,
  uiContainerTransitionMs,
} from "@src/utils";
import { Arrow } from "./arrow";

interface MenuProps {
  title: string;
  menuLinks: Array<MenuLink>;
  accentColorKey?: string;
  size?: MenuItemSize;
  lightIconBg?: boolean;
  layout?: string;
  footerMenu?: boolean;
  highlightSection?: boolean;
}

interface ItemProps {
  item: MenuLink;
  size: MenuItemSize;
  accentColorKey?: string;
  lightIconBg?: boolean;
  highlightSection?: boolean;
}

const HeaderSubMenuItem: FC<ItemProps> = ({
  item,
  size = "default",
  accentColorKey = "",
  lightIconBg = false,
  highlightSection = false,
}) => {
  const accentColor = accentColors[accentColorKey]
    ? accentColors[accentColorKey]
    : "";
  const accentBgColor = accentBackgrounds[accentColorKey]
    ? accentBackgrounds[accentColorKey]
    : "";

  const serviceStatus = item.tag;
  const subTextColor = highlightSection
    ? Colors.PrimaryContrast
    : size === "big"
      ? Colors.HeadingText
      : Colors.BodyText;
  const textColor =
    item.type === "disabled"
      ? Colors.BodyText
      : highlightSection
        ? Colors.PrimaryContrast
        : Colors.HeadingText;

  const iconColor = accentColorKey
    ? accentColors[accentColorKey]
    : Colors.PrimaryMain;

  return (
    <Link
      href={item.type === "disabled" ? undefined : item.link}
      sx={{
        padding: "0",
        textDecoration: "none",
        verticalAlign: "middle",
        ".nav-arrow-forward": {
          color: textColor,
          stroke: textColor,
        },
        ".nav-item-stack": {
          border: `1px solid transparent`,
          transition: `opacity ${uiContainerTransitionMs}ms ease-in-out, background ${uiContainerTransitionMs}ms ease-in-out, border ${uiContainerTransitionMs}ms ease-in-out`,
        },
        ".nav-item-description": {
          color: subTextColor,
          transition: `color ${uiContainerTransitionMs}ms ease-in-out`,
        },
        transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
        opacity: 1,
        "&:hover": {
          opacity: highlightSection ? 0.8 : 1,
          ".nav-item-stack": {
            border: `1px solid ${accentColor}`,
            background: accentBgColor,
          },

          ".nav-item-description": {
            color: highlightSection
              ? Colors.PrimaryContrast
              : Colors.HeadingText,
          },

          ".nav-arrow-forward": {
            opacity: 1,
            transform: `translate3d(0px, 0, 0)`,
          },
        },
      }}
    >
      <Stack
        direction="row"
        gap="16px"
        sx={{
          alignItems: "start",
        }}
      >
        {item?.service !== null && item?.service?.trim() !== "" ? (
          <Stack
            className="nav-item-stack"
            sx={{
              width: size === "big" ? "52px" : "40px",
              height: size === "big" ? "52px" : "40px",
              backgroundColor: lightIconBg
                ? Colors.PrimaryAccentBackground
                : Colors.PrimaryAccentBackgroundLight,
              borderRadius: "8px",
              flex: `0 0 ${size === "big" ? "52px" : "40px"}`,
            }}
            alignItems="center"
            justifyContent="center"
          >
            <ServiceIcon
              service={item?.service || ""}
              disabled={item.type === "disabled"}
              sx={{
                color: iconColor,
                fontSize: size === "default" ? "24px" : "32px",
              }}
              size={size === "default" ? 24 : 32}
              strokeWidth={size === "big" ? "0px" : "0.25px"}
            />
          </Stack>
        ) : (
          ""
        )}
        <Stack direction="column">
          <Stack direction="row">
            <Typography
              variant="overline"
              sx={{
                color: textColor,
                fontSize: size === "big" ? "16px" : "14px",
                marginBottom: "0px",
                fontWeight: "600",
              }}
            >
              {item.name} {serviceStatus?.trim() == "" ? <Arrow /> : ""}
              <span
                style={{
                  color: size === "big" ? "#E0CCFF" : Colors.PrimaryMain,
                }}
              >
                {serviceStatus}
              </span>{" "}
              {serviceStatus?.trim() !== "" ? <Arrow /> : ""}
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            className="nav-item-description"
            sx={{
              lineHeight: "22px",
              fontSize: size === "big" ? "16px" : "14px",
              fontWeight: 500,
            }}
          >
            {item.description}
          </Typography>
        </Stack>
      </Stack>
    </Link>
  );
};

const HeaderSubMenu: FC<MenuProps> = ({
  title,
  menuLinks,
  accentColorKey = "",
  size = "default",
  layout = "",
  lightIconBg = false,
  footerMenu = false,
  highlightSection = false,
}) => {
  if (title === "break") return null;

  let additionalStyles: SxProps = {
    color: highlightSection
      ? Colors.PrimaryContrast
      : accentColors[accentColorKey],
  };

  let filteredItems = menuLinks;

  if (footerMenu === false) {
    filteredItems = menuLinks.filter(filterFooterNavItems);
  }

  if (size === "big") {
    additionalStyles = {
      ...additionalStyles,
      fontSize: "16px",
      fontWeight: "600",
    };
  }

  if (layout === "highlight") {
    return (
      <Stack gap={size === "big" ? "28px" : "20px"}>
        <Typography
          variant="body2"
          sx={{
            ...additionalStyles,
            marginTop: "0px",
            fontSize: "12px",
            letterSpacing: "0.5px",
            fontWeight: "600",
            lineHeight: "20px",
            textTransform: "uppercase",
            color: Colors.HeadingText,
          }}
        >
          {title}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack gap={size === "big" ? "28px" : "20px"}>
      <Typography
        variant="body2"
        sx={{
          ...additionalStyles,
          marginTop: "0px",
          fontSize: size === "big" ? "14px" : "12px",
          letterSpacing: "0.5px",
          fontWeight: 600,
          lineHeight: "20px",
          textTransform: "uppercase",
        }}
      >
        {title}
      </Typography>
      {filteredItems &&
        filteredItems.map((item) => {
          return (
            <HeaderSubMenuItem
              item={item}
              key={item.name}
              size={size}
              accentColorKey={accentColorKey}
              lightIconBg={lightIconBg}
              highlightSection={highlightSection}
            />
          );
        })}
    </Stack>
  );
};

export default HeaderSubMenu;
